"use client";
import React, { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import trackerFeatures from "./trackerFeatures.json";
import SectionHeader from "../../../SectionHeader";
import FeatureTabs from "../FeatureTabs";
import FeatureContent from "../FeatureContent";
import {
    LocationWidget,
    GeofenceWidget,
    FamilySharingWidget,
    EmergencyContactsWidget,
    FindDeviceWidget,
    SmartNotificationsWidget,
} from "../widgets";

const trackerTabsConfig = [
    {
        id: "location",
        iconKey: "MapPin",
        color: "from-sky-400 to-blue-500",
        Widget: LocationWidget,
        scenarioIcons: ["Baby", "User", "Briefcase", "Heart"],
    },
    {
        id: "geofence",
        iconKey: "AlertTriangle",
        color: "from-red-400 to-pink-500",
        Widget: GeofenceWidget,
        scenarioIcons: ["GraduationCap", "Home", "Building", "Sunset"],
    },
    {
        id: "familySharing",
        iconKey: "Users",
        color: "from-indigo-400 to-blue-600",
        Widget: FamilySharingWidget,
        scenarioIcons: ["Users", "User", "TrendingUp", "Shield"],
    },
    {
        id: "emergencyContacts",
        iconKey: "Phone",
        color: "from-rose-400 to-red-600",
        Widget: EmergencyContactsWidget,
        scenarioIcons: ["Phone", "AlertTriangle", "AlertTriangle", "Heart"],
    },
    {
        id: "findDevice",
        iconKey: "Search",
        color: "from-violet-400 to-purple-500",
        Widget: FindDeviceWidget,
        scenarioIcons: ["LocateFixed", "Sofa", "Factory", "MapPin"],
    },
    {
        id: "smartNotifications",
        iconKey: "Bell",
        color: "from-fuchsia-400 to-purple-600",
        Widget: SmartNotificationsWidget,
        scenarioIcons: ["Gamepad2", "Heart", "Calendar", "Heart"],
    },
];

const TrackerFeatures: React.FC = () => {
    const [activeTab, setActiveTab] = useState("location");
    const { t, i18n } = useTranslation("trackerFeatures");

    React.useEffect(() => {
        i18n.addResourceBundle("fa", "trackerFeatures", trackerFeatures.fa, true, true);
        i18n.addResourceBundle("en", "trackerFeatures", trackerFeatures.en, true, true);
    }, [i18n]);

    const tabs = trackerTabsConfig.map((config) => {
        const scenariosData = t(`tabs.${config.id}.scenarios`, { returnObjects: true });
        const scenarios = Array.isArray(scenariosData) ? scenariosData : [];
        return {
            id: config.id,
            title: t(`tabs.${config.id}.title`),
            description: t(`tabs.${config.id}.description`),
            iconKey: config.iconKey,
            color: config.color,
            scenarios: scenarios.map((s: any, index: number) => ({
                title: s.title,
                description: s.description,
                iconKey: config.scenarioIcons[index] || "Check",
            })),
        };
    });

    const activeTabData = tabs.find((tab) => tab.id === activeTab) || tabs[0];
    const ActiveWidget = trackerTabsConfig.find((c) => c.id === activeTab)?.Widget as React.ComponentType<{ color?: string }> | undefined;

    return (
        <section dir="rtl" className="py-16 md:py-28 overflow-hidden">
            <div className="container mx-auto px-4 sm:px-10">
                <SectionHeader
                    regularText={t("tracker.sectionHeader.regularText")}
                    highlightedText={t("tracker.sectionHeader.highlightedText")}
                    descriptionHighlight={t("tracker.sectionHeader.descriptionHighlight")}
                    descriptionText={t("tracker.sectionHeader.descriptionText")}
                />

                <div className="mt-8">
                    <FeatureTabs
                        tabs={tabs}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                    />

                    <div className="flex flex-col md:flex-row items-start gap-8 lg:gap-12 bg-white/50 backdrop-blur-md p-5 md:p-8 rounded-2xl shadow-xl border border-gray-200/50 min-h-[450px]">
                        <FeatureContent activeTab={activeTab} tabs={tabs} />

                        <div className="w-full md:w-1/3 flex justify-center self-center">
                            <AnimatePresence mode="wait">
                                {ActiveWidget && <ActiveWidget key={activeTab + "-widget"} color={activeTabData.color} />}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TrackerFeatures;
